/**
 * Pure, platform-free email helpers.
 *
 * Used both in Angular (admin/app) and in Node (Cloud Functions), so this file
 * MUST NOT import from any platform-specific package (no Angular, no
 * firebase-admin, no DOM/Node globals beyond plain JS).
 */

/**
 * Minimal, pragmatic email format check. Not RFC-complete on purpose: it accepts
 * the common `local@domain.tld` shape and rejects whitespace, missing parts and
 * missing TLD. Good enough to block obvious typos before persisting.
 */
export function isValidEmail(email: string): boolean {
  if (typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (!trimmed) return false;
  // No spaces, exactly one @, non-empty local part, domain with a dot + TLD.
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(trimmed);
}

/**
 * Parses a user-typed string of comma-separated emails into a clean array:
 *  - splits on commas (and semicolons, a common paste artefact),
 *  - trims each entry,
 *  - drops empty entries,
 *  - de-duplicates case-insensitively (keeping the first occurrence as typed).
 *
 * Does NOT validate format — callers validate with `isValidEmail` when needed.
 */
export function parseEmailList(input: string): string[] {
  if (typeof input !== 'string') return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input.split(/[,;]/)) {
    const email = raw.trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(email);
  }
  return result;
}
